import { DelayDayOfWeek, DelayMode, TimerDelayData } from "../types";

type TimeDelay = Pick<
  TimerDelayData,
  "days_delay" | "hours_delay" | "minutes_delay"
>;

/** "1D 2H 30M" from the non-zero parts of a wait; "-" when nothing is set. */
export const formatTimeDelay = (delay?: TimeDelay | null): string => {
  const parts: [number | null | undefined, string][] = [
    [delay?.days_delay, "D"],
    [delay?.hours_delay, "H"],
    [delay?.minutes_delay, "M"],
  ];
  const label = parts
    .filter(([value]) => !!value)
    .map(([value, unit]) => `${String(value)}${unit}`)
    .join(" ");
  return label || "-";
};

// ---------------------------------------------------------------------------
// "Delay until" modes (tinyemail/tiny-platform#2350)
// ---------------------------------------------------------------------------

export const DAYS_OF_WEEK: { value: DelayDayOfWeek; label: string }[] = [
  { value: "MONDAY", label: "Mon" },
  { value: "TUESDAY", label: "Tue" },
  { value: "WEDNESDAY", label: "Wed" },
  { value: "THURSDAY", label: "Thu" },
  { value: "FRIDAY", label: "Fri" },
  { value: "SATURDAY", label: "Sat" },
  { value: "SUNDAY", label: "Sun" },
];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Absent (nodes saved before #2350) or null means RELATIVE. */
export const getDelayMode = (data?: TimerDelayData | null): DelayMode =>
  data?.delay_mode || "RELATIVE";

const parseTime = (time?: string | null) => {
  const match = time ? TIME_PATTERN.exec(time) : null;
  return match ? { hours: Number(match[1]), minutes: Number(match[2]) } : null;
};

const parseDate = (date?: string | null) => {
  const match = date ? DATE_PATTERN.exec(date) : null;
  if (!match) return null;
  const [year, month, day] = [
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
  ];
  // Reject 2026-02-31 and the like, which Date.UTC would quietly roll over.
  const check = new Date(Date.UTC(year, month - 1, day));
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
};

/** "16:30" -> "4:30 PM". */
export const formatTime12h = (time?: string | null): string => {
  const parsed = parseTime(time);
  if (!parsed) return "-";
  const hour12 = parsed.hours % 12 || 12;
  const suffix = parsed.hours < 12 ? "AM" : "PM";
  return `${hour12}:${String(parsed.minutes).padStart(2, "0")} ${suffix}`;
};

/** The drawer's hour (1-12) / minute / "am"|"pm" selects -> "HH:mm". */
export const toTime24h = (hour: number, minute: number, ampm: string) => {
  const hours = (hour % 12) + (ampm === "pm" ? 12 : 0);
  return `${String(hours).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

/** "HH:mm" -> the drawer's hour (1-12) / minute / "am"|"pm" selects. */
export const fromTime24h = (time?: string | null) => {
  const parsed = parseTime(time);
  if (!parsed) return undefined;
  return {
    hour: parsed.hours % 12 || 12,
    minute: parsed.minutes,
    ampm: parsed.hours < 12 ? "am" : "pm",
  };
};

/**
 * Intl.DateTimeFormat construction is slow, and the time-zone picker labels
 * ~400 zones, so each zone's formatters are built once and reused. Throws, like
 * the constructor, for a zone the engine does not know.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();
const formatterFor = (
  kind: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat => {
  const key = `${kind}|${timeZone}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", { ...options, timeZone });
    formatters.set(key, formatter);
  }
  return formatter;
};

const WALL_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

const isValidTimeZone = (timeZone?: string | null): timeZone is string => {
  if (!timeZone) return false;
  try {
    formatterFor("wall", timeZone, WALL_TIME_OPTIONS);
    return true;
  } catch {
    return false;
  }
};

/**
 * The wall-clock parts of an instant in a zone, read with formatToParts so no
 * locale's date layout is relied on.
 */
const wallTimeOf = (timeZone: string, instant: number) => {
  const parts = formatterFor("wall", timeZone, WALL_TIME_OPTIONS).formatToParts(
    new Date(instant)
  );
  const get = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
};

/** Milliseconds the zone is ahead of UTC at the given instant. */
const zoneOffsetMs = (timeZone: string, instant: number): number => {
  const wall = wallTimeOf(timeZone, instant);
  const asUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second
  );
  return asUtc - (instant - (instant % 1000));
};

/**
 * The instant a wall-clock date + time occurs in an IANA zone, resolved the way
 * the backend's java.time.ZonedDateTime does: a time inside a spring-forward gap
 * moves forward by the gap, and a time inside a fall-back overlap takes the
 * earlier offset. Undefined when any part is missing or invalid.
 */
export const zonedWallTimeToInstant = (
  date?: string | null,
  time?: string | null,
  timeZone?: string | null
): Date | undefined => {
  const day = parseDate(date);
  const clock = parseTime(time);
  if (!day || !clock || !isValidTimeZone(timeZone)) return undefined;
  const wall = Date.UTC(
    day.year,
    day.month - 1,
    day.day,
    clock.hours,
    clock.minutes
  );
  // Offsets just before and after any transition near this wall time; the
  // earlier one is tried first so an overlap resolves to it.
  const before = zoneOffsetMs(timeZone, wall - 86400000);
  const after = zoneOffsetMs(timeZone, wall + 86400000);
  for (const offset of before === after ? [before] : [before, after]) {
    const candidate = wall - offset;
    if (zoneOffsetMs(timeZone, candidate) === offset) {
      return new Date(candidate);
    }
  }
  // In a gap: neither offset reproduces the wall time. Using the offset from
  // before the transition lands the same distance past it - java.time's rule.
  return new Date(wall - before);
};

/** "EDT" / "EST", or the offset ("GMT+5:30") for zones without a common one. */
export const getTimeZoneAbbreviation = (
  timeZone: string,
  at: Date = new Date()
): string => {
  try {
    return (
      formatterFor("short", timeZone, { timeZoneName: "short" })
        .formatToParts(at)
        .find((part) => part.type === "timeZoneName")?.value ?? timeZone
    );
  } catch {
    return timeZone;
  }
};

const cityOf = (timeZone: string) =>
  (timeZone.split("/").pop() || timeZone).replace(/_/g, " ");

/**
 * "Eastern Time — New York (EDT)". Engines without the "shortGeneric"/
 * "longGeneric" zone names, and zones that only have an offset name, fall back
 * to "America/New_York (EDT)".
 */
export const formatTimeZoneLabel = (
  timeZone: string,
  at: Date = new Date()
): string => {
  const abbreviation = getTimeZoneAbbreviation(timeZone, at);
  try {
    // "longGeneric" is newer than this TypeScript's Intl typings.
    const generic = formatterFor("longGeneric", timeZone, {
      timeZoneName: "longGeneric",
    } as unknown as Intl.DateTimeFormatOptions)
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value;
    if (generic && !generic.startsWith("GMT") && timeZone.includes("/")) {
      return `${generic} — ${cityOf(timeZone)} (${abbreviation})`;
    }
  } catch {
    // fall through
  }
  return `${timeZone} (${abbreviation})`;
};

const FALLBACK_TIME_ZONES = [
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Halifax",
  "America/Sao_Paulo",
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Africa/Johannesburg",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

const FIXED_OFFSET_ALIASES = new Set([
  "EST",
  "MST",
  "HST",
  "GMT",
  "GMT0",
  "GMT+0",
  "GMT-0",
  "Greenwich",
  "UCT",
  "Universal",
  "Zulu",
]);

/**
 * Legacy ids whose rules are a single fixed offset (EST, Etc/GMT+5, ...). The
 * backend rejects them because they never follow DST; UTC and Etc/UTC are the
 * only fixed zones it accepts.
 */
export const isFixedOffsetTimeZone = (timeZone: string): boolean =>
  timeZone !== "UTC" &&
  timeZone !== "Etc/UTC" &&
  (timeZone.startsWith("Etc/") || FIXED_OFFSET_ALIASES.has(timeZone));

/** The zone itself, or "UTC" when the backend would reject it. */
export const toSupportedTimeZone = (timeZone: string): string =>
  isFixedOffsetTimeZone(timeZone) ? "UTC" : timeZone;

/** The browser's IANA zone, which the new modes default to. */
export const getBrowserTimeZone = (): string => {
  try {
    return toSupportedTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    );
  } catch {
    return "UTC";
  }
};

/**
 * Every IANA zone the browser knows, or a short list where
 * Intl.supportedValuesOf is missing. `include` (the saved zone or the browser's,
 * which may be an alias the list omits) is always present.
 */
export const listTimeZones = (include?: string | null): string[] => {
  const supportedValuesOf = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  let zones = FALLBACK_TIME_ZONES;
  if (typeof supportedValuesOf === "function") {
    try {
      zones = supportedValuesOf("timeZone");
    } catch {
      // keep the fallback list
    }
  }
  zones = zones.filter((zone) => !isFixedOffsetTimeZone(zone));
  return include && !zones.includes(include) ? [include, ...zones] : zones;
};

const formatDaysOfWeek = (days?: DelayDayOfWeek[] | null): string | null => {
  const labels = DAYS_OF_WEEK.filter(({ value }) => days?.includes(value)).map(
    ({ label }) => label
  );
  if (labels.length === 0) return null;
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
};

/**
 * What a delay waits for, without a prefix: "1D 2H 30M", "4:30 PM EDT",
 * "Nov 3, 2026, 9:00 AM EST", "next Tue or Thu, 9:00 AM EDT". The abbreviation
 * is taken at the target date for a specific date and at `now` otherwise. "-"
 * when the settings are incomplete.
 */
export const formatDelayTarget = (
  data?: TimerDelayData | null,
  now: Date = new Date()
): string => {
  const mode = getDelayMode(data);
  if (mode === "RELATIVE") return formatTimeDelay(data);

  const timeZone = data?.delay_time_zone;
  if (!parseTime(data?.delay_time) || !isValidTimeZone(timeZone)) return "-";
  const time = formatTime12h(data?.delay_time);

  if (mode === "TIME_OF_DAY") {
    return `${time} ${getTimeZoneAbbreviation(timeZone, now)}`;
  }
  if (mode === "SPECIFIC_DATE") {
    const day = parseDate(data?.delay_date);
    const instant = zonedWallTimeToInstant(
      data?.delay_date,
      data?.delay_time,
      timeZone
    );
    if (!day || !instant) return "-";
    return `${MONTHS[day.month - 1]} ${day.day}, ${
      day.year
    }, ${time} ${getTimeZoneAbbreviation(timeZone, instant)}`;
  }
  const days = formatDaysOfWeek(data?.delay_days_of_week);
  if (!days) return "-";
  return `next ${days}, ${time} ${getTimeZoneAbbreviation(timeZone, now)}`;
};

/**
 * The node card's title line. Relative keeps "Time Delay : 1D 2H 30M". A
 * Specific date that has passed shows the date a contact arriving now waits
 * for: the same date and time in the first later year that is after `now`.
 */
export const formatDelayCardLabel = (
  data?: (TimerDelayData & { label?: string }) | null,
  now: Date = new Date()
): string => {
  if (getDelayMode(data) === "RELATIVE") {
    return `${data?.label ?? "Time Delay"} : ${formatTimeDelay(data)}`;
  }
  const nextDate = getNextSpecificDate(data, now);
  const target = nextDate ? { ...data, delay_date: nextDate } : data;
  return `Delay until ${formatDelayTarget(target, now)}`;
};

/** Short form for the publish dialog's "old → new": "3D", "until 4:30 PM EDT". */
export const formatDelaySummary = (
  data?: TimerDelayData | null,
  now: Date = new Date()
): string => {
  if (!data) return "-";
  const target = formatDelayTarget(data, now);
  return getDelayMode(data) === "RELATIVE" || target === "-"
    ? target
    : `until ${target}`;
};

/** A Specific-date delay whose moment is at or before `now`. */
export const isSpecificDatePassed = (
  data?: TimerDelayData | null,
  now: Date = new Date()
): boolean => {
  if (getDelayMode(data) !== "SPECIFIC_DATE") return false;
  const instant = zonedWallTimeToInstant(
    data?.delay_date,
    data?.delay_time,
    data?.delay_time_zone
  );
  return !!instant && instant.getTime() <= now.getTime();
};

/**
 * A Specific date's "YYYY-MM-DD", or the same date in the first later year
 * whose moment (in the node's zone, at that year's offset) is after `now`. As
 * the backend's DelayRelease.nextSpecificMoment, the years are added to the
 * configured date, so 29 Feb is 28 Feb in a non-leap year and 29 Feb again in
 * the next leap year. Null for other modes or incomplete settings.
 */
export const getNextSpecificDate = (
  data?: TimerDelayData | null,
  now: Date = new Date()
): string | null => {
  if (getDelayMode(data) !== "SPECIFIC_DATE") return null;
  const date = data?.delay_date;
  const day = parseDate(date);
  if (
    !date ||
    !day ||
    !zonedWallTimeToInstant(date, data?.delay_time, data?.delay_time_zone)
  ) {
    return null;
  }
  // Start a year short of now's year, as the backend does, so a date many years
  // back does not loop through each one.
  let years = Math.max(0, now.getUTCFullYear() - day.year - 1);
  for (;;) {
    const candidate = addYearsToIsoDate(date, years);
    const instant = zonedWallTimeToInstant(
      candidate,
      data?.delay_time,
      data?.delay_time_zone
    );
    if (instant && instant.getTime() > now.getTime()) return candidate;
    years++;
  }
};

export const DATE_PASSED_WARNING =
  "Date passed — contacts wait until the same date next year";

// ---------------------------------------------------------------------------
// Drawer form <-> node data
// ---------------------------------------------------------------------------

export interface DelayFormValues {
  mode: DelayMode;
  days: number;
  hours: number;
  minutes: number;
  /** 1-12 */
  hour: number;
  minute: number;
  ampm: string;
  /** "YYYY-MM-DD" */
  date: string | null;
  daysOfWeek: DelayDayOfWeek[];
  timeZone: string;
}

const DEFAULT_TIME = { hour: 9, minute: 0, ampm: "am" };

export const toDelayFormValues = (
  data: TimerDelayData | null | undefined,
  defaultTimeZone: string
): DelayFormValues => ({
  mode: getDelayMode(data),
  days: data?.days_delay || 0,
  hours: data?.hours_delay || 0,
  minutes: data?.minutes_delay || 0,
  ...(fromTime24h(data?.delay_time) ?? DEFAULT_TIME),
  date: data?.delay_date || null,
  daysOfWeek: data?.delay_days_of_week ?? [],
  timeZone: data?.delay_time_zone || defaultTimeZone,
});

/**
 * The node data to save for the selected mode. updateNode merges this over the
 * old data, so every field another mode owns is sent as null to clear it.
 */
export const toDelayNodeData = (
  values: DelayFormValues
): Required<TimerDelayData> => {
  const { mode } = values;
  const absolute = mode !== "RELATIVE";
  return {
    delay_mode: mode,
    days_delay: absolute ? null : values.days,
    hours_delay: absolute ? null : values.hours,
    minutes_delay: absolute ? null : values.minutes,
    delay_time: absolute
      ? toTime24h(values.hour, values.minute, values.ampm)
      : null,
    delay_date: mode === "SPECIFIC_DATE" ? values.date : null,
    delay_days_of_week:
      mode === "DAY_OF_WEEK"
        ? DAYS_OF_WEEK.map(({ value }) => value).filter((day) =>
            values.daysOfWeek.includes(day)
          )
        : null,
    delay_time_zone: absolute ? values.timeZone : null,
  };
};

/** The backend rejects a Specific date more than this far ahead. */
export const MAX_SPECIFIC_DATE_YEARS = 2;

export const DATE_IN_PAST_ERROR = "Choose a date and time in the future";
export const DATE_TOO_FAR_ERROR = `Choose a date within the next ${MAX_SPECIFIC_DATE_YEARS} years`;

/** Today's date ("YYYY-MM-DD") in an IANA zone, which may differ from the browser's. */
export const getZoneToday = (timeZone: string, now: Date = new Date()) => {
  const zone = isValidTimeZone(timeZone) ? timeZone : getBrowserTimeZone();
  const { year, month, day } = wallTimeOf(zone, now.getTime());
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;
};

/** "2026-10-21" + 2 -> "2028-10-21"; Feb 29 clamps to Feb 28. */
export const addYearsToIsoDate = (date: string, years: number): string => {
  const parsed = parseDate(date);
  if (!parsed) return date;
  const year = parsed.year + years;
  const lastDay = new Date(Date.UTC(year, parsed.month, 0)).getUTCDate();
  const day = Math.min(parsed.day, lastDay);
  return `${year}-${String(parsed.month).padStart(2, "0")}-${String(
    day
  ).padStart(2, "0")}`;
};

/** Latest Specific date the backend accepts, in the chosen zone. */
export const getMaxSpecificDate = (timeZone: string, now: Date = new Date()) =>
  addYearsToIsoDate(getZoneToday(timeZone, now), MAX_SPECIFIC_DATE_YEARS);

/**
 * When the time-zone picker's labels must be rebuilt: whenever the instant
 * their abbreviation is taken at changes. That is the target itself for a
 * specific date, otherwise `now` to the minute - so a DST change during the
 * day is picked up without rebuilding ~400 labels on every render.
 */
export const getTimeZoneLabelKey = (
  target: Date | undefined,
  now: Date = new Date()
): number => (target ? target.getTime() : Math.floor(now.getTime() / 60000));

/**
 * The latest Specific moment the backend accepts: now in the chosen zone plus
 * MAX_SPECIFIC_DATE_YEARS, as `now.atZone(zone).plusYears(2)`. Truncated to the
 * minute, so it is never later than the backend's bound.
 */
const getMaxSpecificInstant = (timeZone: string, now: Date) => {
  const wall = wallTimeOf(timeZone, now.getTime());
  const time = `${String(wall.hour).padStart(2, "0")}:${String(
    wall.minute
  ).padStart(2, "0")}`;
  return zonedWallTimeToInstant(
    getMaxSpecificDate(timeZone, now),
    time,
    timeZone
  );
};

/**
 * Compares moments, not dates, like the backend: on the max date itself a time
 * later in the day than now is already too far.
 */
const isBeyondMaxDate = (values: DelayFormValues, now: Date) => {
  if (!isValidTimeZone(values.timeZone)) return false;
  const moment = zonedWallTimeToInstant(
    values.date,
    toTime24h(values.hour, values.minute, values.ampm),
    values.timeZone
  );
  const max = getMaxSpecificInstant(values.timeZone, now);
  return !!moment && !!max && moment.getTime() > max.getTime();
};

/** Whether the selected mode's settings can be saved. */
export const isDelayFormValid = (
  values: DelayFormValues,
  now: Date = new Date()
): boolean => {
  if (values.mode === "RELATIVE") {
    // The backend's 1-minute minimum.
    return !!(values.days || values.hours || values.minutes);
  }
  const data = toDelayNodeData(values);
  if (!parseTime(data.delay_time) || !isValidTimeZone(data.delay_time_zone)) {
    return false;
  }
  if (values.mode === "SPECIFIC_DATE") {
    return (
      !!parseDate(data.delay_date) &&
      !isSpecificDatePassed(data, now) &&
      !isBeyondMaxDate(values, now)
    );
  }
  if (values.mode === "DAY_OF_WEEK") {
    return (data.delay_days_of_week ?? []).length > 0;
  }
  return true;
};

export interface DelayDateNote {
  severity: "warning" | "error";
  message: string;
}

/**
 * Save state and the Specific-date note for the drawer. An unchanged node
 * whose date has passed gets the informational warning; an edit to a moment
 * that is not in the future, or beyond the backend's limit, is an error.
 */
export const getDelayFormState = (
  initial: DelayFormValues,
  current: DelayFormValues,
  now: Date = new Date()
): { saveDisabled: boolean; dateNote: DelayDateNote | null } => {
  const saved = toDelayNodeData(current);
  // Compare what would be saved, not the raw form, so switching mode and back
  // (or touching a field the mode ignores) does not enable Save.
  const unchanged =
    JSON.stringify(toDelayNodeData(initial)) === JSON.stringify(saved);
  const saveDisabled = unchanged || !isDelayFormValid(current, now);

  let dateNote: DelayDateNote | null = null;
  if (current.mode === "SPECIFIC_DATE" && parseDate(current.date)) {
    if (isSpecificDatePassed(saved, now)) {
      dateNote = unchanged
        ? { severity: "warning", message: DATE_PASSED_WARNING }
        : { severity: "error", message: DATE_IN_PAST_ERROR };
    } else if (isBeyondMaxDate(current, now)) {
      dateNote = { severity: "error", message: DATE_TOO_FAR_ERROR };
    }
  }
  return { saveDisabled, dateNote };
};
